import React, { useRef, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import { useCounterpartyDetailsQuery } from "@api/counterparty.api";
import { CreateCounterparty } from "@components/counterparty/CreateCounterparty";
import {
    CounterpartyFormFields,
    FormSchema,
} from "@components/counterparty/CounterpartyForm";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EditCounterparty } from "../EditCounterparty";
import { CounterpartyAddressesSection } from "./CounterpartyAddressesSection";
import { CounterpartyContactsSection } from "./CounterpartyContactsSection";
import { CounterpartyEditorModalFooter } from "./CounterpartyEditorModalFooter";
import { useOcrSeedBooks } from "./useOcrSeedBooks";

interface Props {
    onCreated: (response: CounterpartyCreationResponse) => void;
    onCancelled: () => void;
    initialValues?: Partial<FormSchema>;
    seedContacts?: CreateCounterpartyContactBody[];
    seedAddresses?: CreateCounterpartyAddressBody[];
    fields?: CounterpartyFormFields;
    forTransactionEditing?: boolean;
    sectionClassName?: string;
    formId?: string;
    footer?: "form" | "none";
    dirtyRef?: React.MutableRefObject<boolean>;
    onChildEditingChange?: (isEditing: boolean) => void;
}

export const CounterpartyEditorDrawerCreate: React.FC<Props> = ({
    onCreated,
    onCancelled,
    initialValues,
    seedContacts = [],
    seedAddresses = [],
    fields = "company",
    forTransactionEditing,
    sectionClassName,
    formId,
    footer = "none",
    dirtyRef,
    onChildEditingChange,
}) => {
    const [created, setCreated] = useState<Counterparty | null>(null);
    const [isContactEditing, setIsContactEditing] = useState(false);
    const [isAddressEditing, setIsAddressEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const stayOpenRef = useRef(false);
    const createdIdRef = useRef<string | null>(null);
    // Holds the just-created counterparty when creation happened as a side effect
    // of adding the first contact/address, so the swap from CreateCounterparty to
    // EditCounterparty is deferred until that child modal closes (see handleCreated).
    const pendingCreatedRef = useRef<Counterparty | null>(null);
    const submitRef = useRef<(() => Promise<void>) | null>(null);
    // Dedupe concurrent creations: the Contacts and Addresses sections each
    // call ensureCreated on save. Without an in-flight guard, two child adds
    // fired before the first create resolves would both see a null id and
    // create duplicate counterparties (mirrors copyPromiseRef in the Edit path).
    const createPromiseRef = useRef<Promise<string> | null>(null);
    const { error: showErrorToast } = useToaster();
    const seeds = useOcrSeedBooks({
        contacts: seedContacts,
        addresses: seedAddresses,
    });
    const detailsQuery = useCounterpartyDetailsQuery({
        counterpartyId: created?.id,
        enabled: Boolean(created),
    });

    const handleCreated = async (response: CounterpartyCreationResponse) => {
        createdIdRef.current = response.counterparty.id;
        const didSeedSaveFail = await seeds.persist(response.counterparty.id);
        if (didSeedSaveFail) {
            showErrorToast(
                "Created counterparty, but couldn't save some extracted contact details",
            );
        }
        if (!stayOpenRef.current) {
            setCreated(response.counterparty);
            await onCreated(response);
            return;
        }
        // Created as a side effect of adding the first contact or address. Defer
        // the swap from CreateCounterparty to EditCounterparty until that child
        // modal closes: swapping now unmounts the open child editor while its
        // write is still in flight, discarding it (and the user's input) if the
        // write fails. The swap runs from reportChildEditing once editing settles.
        pendingCreatedRef.current = response.counterparty;
        stayOpenRef.current = false;
    };

    const ensureCreated = (): Promise<string> => {
        if (createdIdRef.current) {
            return Promise.resolve(createdIdRef.current);
        }
        if (createPromiseRef.current) {
            return createPromiseRef.current;
        }

        const promise = (async () => {
            stayOpenRef.current = true;
            try {
                // Route parent creation through the form's submit so it runs the
                // same Zod validation and emptyToNull normalization as a manual
                // Add, instead of posting raw field values.
                await submitRef.current?.();
            } catch (error) {
                stayOpenRef.current = false;
                throw error;
            }

            const createdId = createdIdRef.current;
            if (!createdId) {
                // submitForm resolves even when validation blocks creation; its
                // field errors render on the form hidden behind the child modal,
                // so surface a toast to explain why nothing was added.
                stayOpenRef.current = false;
                showErrorToast(
                    "Add the counterparty's display name before adding contacts or addresses",
                );
                throw new Error("Counterparty creation was not completed");
            }

            return createdId;
        })();

        createPromiseRef.current = promise;
        // Allow a retry after a failed attempt instead of caching the rejection.
        void promise.catch(() => {
            if (createPromiseRef.current === promise) {
                createPromiseRef.current = null;
            }
        });

        return promise;
    };

    const reportChildEditing = ({
        contact,
        address,
    }: {
        contact: boolean;
        address: boolean;
    }) => {
        const isAnyChildEditing = contact || address;
        onChildEditingChange?.(isAnyChildEditing);
        // The child modal has closed after a deferred creation; now it's safe to
        // swap to EditCounterparty without unmounting an open child editor.
        if (!isAnyChildEditing && pendingCreatedRef.current) {
            setCreated(pendingCreatedRef.current);
            pendingCreatedRef.current = null;
        }
    };

    const contacts = created
        ? (detailsQuery.data?.contacts ?? [])
        : seeds.contactRows;
    const addresses = created
        ? (detailsQuery.data?.addresses ?? [])
        : seeds.addressRows;
    const counterpartyId = created?.id ?? "";

    // Order matches Edit everywhere: Basic info, Contacts, Addresses, Tax, Note.
    const contactsAndAddresses = (
        <>
            <CounterpartyContactsSection
                counterpartyId={counterpartyId}
                contacts={contacts}
                localBook={created ? undefined : seeds.contactHandlers}
                suggestedIds={created ? undefined : seeds.suggestedContactIds}
                resolveWritableCounterpartyId={ensureCreated}
                onEditingChange={(isEditing) => {
                    setIsContactEditing(isEditing);
                    reportChildEditing({
                        contact: isEditing,
                        address: isAddressEditing,
                    });
                }}
            />
            <CounterpartyAddressesSection
                counterpartyId={counterpartyId}
                addresses={addresses}
                localBook={created ? undefined : seeds.addressHandlers}
                suggestedIds={created ? undefined : seeds.suggestedAddressIds}
                resolveWritableCounterpartyId={ensureCreated}
                onEditingChange={(isEditing) => {
                    setIsAddressEditing(isEditing);
                    reportChildEditing({
                        contact: isContactEditing,
                        address: isEditing,
                    });
                }}
            />
        </>
    );

    return (
        <>
            {created ? (
                <EditCounterparty
                    key={created.id}
                    counterparty={created}
                    onEdited={(counterparty) => onCreated({ counterparty })}
                    onCancelled={onCancelled}
                    fullWidth
                    fields={fields}
                    dirtyRef={dirtyRef}
                    onSubmittingChange={setIsSubmitting}
                    formId={formId}
                    footer="none"
                    sectionClassName={sectionClassName}
                    betweenBasicInfoAndTax={contactsAndAddresses}
                />
            ) : (
                <CreateCounterparty
                    onCreated={handleCreated}
                    onCancelled={onCancelled}
                    initialValues={initialValues}
                    fullWidth
                    forTransactionEditing={forTransactionEditing}
                    sectionClassName={sectionClassName}
                    formId={formId}
                    footer="none"
                    dirtyRef={dirtyRef}
                    submitRef={submitRef}
                    onSubmittingChange={setIsSubmitting}
                    fields={fields}
                    betweenBasicInfoAndTax={contactsAndAddresses}
                />
            )}
            {footer === "form" && (
                <CounterpartyEditorModalFooter
                    formId={formId}
                    submitLabel="Add"
                    isSubmitting={isSubmitting}
                    onCancel={onCancelled}
                />
            )}
        </>
    );
};
